import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import { useQueryClient } from "@tanstack/react-query";

import { ProviderType } from "components/integrations/managed_integrations/types";
import { FileUploadStatus, FileWithDirectoryPath } from "lib/hooks/api/customer_documents/types";
import { useActiveUploads } from "lib/hooks/api/customer_documents/useActiveUploads";
import { useCustomerDocumentProcessingStatus } from "lib/hooks/api/customer_documents/useCustomerDocumentProcessingStatus";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useHoldStaleBundleReload } from "lib/staleBundleReload";
import { UploadProgressBar } from "./UploadProgressBar";

type SyncToProcess = { id: string; integration: ProviderType };

export type UpdateFileStatus = ({
  operationId,
  fileName,
  documentId,
  updates,
}: {
  // The operation the file belongs to (bidId, or the user's guid for
  // content-library uploads). When supplied, matching is confined to that one
  // operation so a fileName fallback can't attach to a same-named file in a
  // different concurrent operation. Omit only when matching purely by the
  // globally-unique documentId
  operationId?: string | null;
  fileName: string | null | undefined;
  documentId: string | null | undefined;
  updates: Partial<FileUploadStatus>;
}) => void;

/**
 * Checks the overall upload and processing status for a group of files.
 * Exported so callers that read statuses in bulk (e.g. the bid-orchestrator
 * driver iterating every queued bid) can derive progress without a per-resource
 * `useDocumentUploadProgress` hook.
 */
export function getUploadProgressStatus(statuses: FileUploadStatus[]) {
  let allUploaded = true;
  let allFinished = true;
  let allProcessed = true;

  for (const status of statuses) {
    const isUploaded = status.isUploaded || !!status.isFinished;
    const isFinished = !!status.isFinished;
    const isProcessed = !!status.isProcessed;

    allUploaded &&= isUploaded;
    allFinished &&= isFinished;
    allProcessed &&= isProcessed;

    if (!allUploaded && !allFinished && !allProcessed) break;
  }

  return { allUploaded, allFinished, allProcessed };
}

/**
 * Decision for the DB-backed completion backstop (manual uploads only).
 *
 * Files are normally driven to `isFinished` by per-file
 * `customerDocumentProcessingStatus` events. If any are missed — e.g. a
 * cancelled orchestrator run, or a doc the workflow skips because it was already
 * processed — an operation would stay stuck processing. `activeUploads` is
 * authoritative: it returns a group only while something is still in-flight, so
 * once a group we have *seen* disappears, every doc has settled and any
 * unfinished stragglers can be marked done.
 *
 * `hasObservedGroup` is the caller's memory that the operation's group has been
 * seen at least once. Without it, a poll landing before the backend registers
 * the upload (group not yet present) would look identical to "all settled" and
 * finish the files prematurely.
 */
export function backstopShouldFinish({
  filesProcessing,
  isManualUpload,
  files,
  operationGroupPresent,
  hasObservedGroup,
}: {
  filesProcessing: boolean;
  isManualUpload: boolean;
  files: FileUploadStatus[];
  operationGroupPresent: boolean;
  hasObservedGroup: boolean;
}): boolean {
  if (!filesProcessing || !isManualUpload) return false;
  if (operationGroupPresent) return false; // still in-flight — nothing to backstop yet
  // An operation re-adopted from the DB has no per-file detail, so an empty
  // `files` means "nothing left to wait for", not "nothing to finish".
  return hasObservedGroup && (files.length === 0 || files.some((f) => !f.isFinished));
}

type UploadSource = "manual" | "googledrive" | "sharepoint";

export type FailedFile = {
  fileName: string;
  recordId: string;
  failureReason?: string;
};

// State for one in-flight upload "operation". An operation is keyed by
// operationId: the bidId for bid-document uploads, or the current user's guid
// for content-library + integration (user-scoped) uploads. The provider tracks
// many of these at once so progress survives navigation between routes.
type OperationState = {
  operationId: string;
  source: UploadSource;
  totalFiles: number;
  processedFiles: number;
  seenFileIds: Set<string>;
  seenSyncIds: Set<string>;
  files: FileUploadStatus[];
  failedFiles: FailedFile[];
  /** True until every file has settled (processed or errored). */
  filesProcessing: boolean;
  /** Whether the progress bar for this operation is shown. */
  show: boolean;
  /** True when the sync itself failed before we received a totalFiles count. */
  syncFailed: boolean;
};

type OperationsMap = Record<string, OperationState>;

// Drops an operation from the map, leaving it untouched when there's nothing to
// remove so React can skip the re-render.
const withoutOperation = (operations: OperationsMap, operationId: string): OperationsMap => {
  if (!operations[operationId]) return operations;
  const next = { ...operations };
  delete next[operationId];
  return next;
};

// What a single consumer sees for its operationId. Reads are scoped; actions on
// the context (below) are global and take an operationId explicitly.
type ScopedProgress = {
  filesProcessing: boolean;
  uploadStatuses: FileUploadStatus[];
  allUploaded: boolean;
  allFinished: boolean;
  allProcessed: boolean;
  filesCount: number;
};

type DocumentUploadProgressContextType = {
  operations: OperationsMap;
  // Start a new integration sync. operationId defaults to the current user's guid.
  setSync: (sync: SyncToProcess | null, operationId?: string) => void;
  // Start manual upload processing for a given operationId (bidId, or the
  // current user's guid for content-library uploads when omitted).
  startProcessing: (files: FileWithDirectoryPath[], operationId?: string) => void;
  // Locates the file by documentId/fileName across all operations — callers
  // don't need to know which operation it belongs to.
  updateFileStatus: UpdateFileStatus;
  addStatusesForFiles: (newFiles: FileWithDirectoryPath[], operationId?: string) => void;
  removeStatusForFile: (fileName: string, operationId?: string) => void;
  dismissOperation: (operationId: string) => void;
};

const DocumentUploadProgressContext = createContext<DocumentUploadProgressContextType | null>(null);

const newOperation = (operationId: string, source: UploadSource): OperationState => ({
  operationId,
  source,
  totalFiles: 0,
  processedFiles: 0,
  seenFileIds: new Set<string>(),
  seenSyncIds: new Set<string>(),
  files: [],
  failedFiles: [],
  filesProcessing: true,
  show: true,
  syncFailed: false,
});

const blankFileStatus = (file: FileWithDirectoryPath) => ({
  file,
  isError: false,
  isUploaded: false,
  isProcessed: false,
  isFinished: false,
  documentId: null,
});

const IDLE_SCOPE: ScopedProgress = {
  filesProcessing: false,
  uploadStatuses: [],
  allUploaded: false,
  allFinished: false,
  allProcessed: false,
  filesCount: 0,
};

// Renders nothing; opens the processing-status subscription for one operation
// and routes its events back into the operations map. One is rendered per
// active operation so many uploads can stream concurrently (mirrors how
// BidOrchestratorStreamProvider fans out subscriptions per bid).
function OperationProcessingSubscription({
  operationId,
  source,
  onUpdate,
  onIntegrationEmptyComplete,
  onFileCompleted,
}: {
  operationId: string;
  source: UploadSource;
  onUpdate: (operationId: string, update: ProcessingStatusUpdate) => void;
  onIntegrationEmptyComplete: (operationId: string) => void;
  onFileCompleted: (recordId: string) => void;
}) {
  const { data: currentUser } = useCurrentUser();
  useCustomerDocumentProcessingStatus({
    operationId,
    enabled: Boolean(operationId && currentUser),
    onData: async (progressUpdate) => {
      if (!progressUpdate) return;
      const { status, totalFiles } = progressUpdate;
      onUpdate(operationId, progressUpdate);

      const isIntegration = source === "googledrive" || source === "sharepoint";
      if (status === "completed" && !totalFiles && isIntegration) {
        onIntegrationEmptyComplete(operationId);
        return;
      }
      if (status === "completed") onFileCompleted(progressUpdate.recordId);
    },
  });
  return null;
}

type ProcessingStatusUpdate = Parameters<
  NonNullable<Parameters<typeof useCustomerDocumentProcessingStatus>[0]["onData"]>
>[0];

export function DocumentUploadProgressProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const [operations, setOperations] = useState<OperationsMap>({});

  // Per-operation latches kept out of render state. Cleared when an operation
  // restarts or is dismissed.
  const completionHandledRef = useRef<Record<string, boolean>>({});
  const observedGroupRef = useRef<Record<string, boolean>>({});
  const verifyingRef = useRef<Record<string, boolean>>({});
  const invalidatedFileIdsRef = useRef<Set<string>>(new Set());

  const anyProcessing = useMemo(
    () => Object.values(operations).some((op) => op.filesProcessing),
    [operations],
  );
  // The browser itself sends the files to S3 and starts their processing.
  useHoldStaleBundleReload(anyProcessing);

  // Single app-wide poll. activeUploads is already user-wide (returns every
  // in-flight group across bids + content library), so one poll feeds every
  // operation and doubles as the cross-navigation reconnection source.
  const { groups, refetch: refetchActiveUploads } = useActiveUploads({
    enabled: Boolean(currentUser),
    // Poll while anything is processing so the completion backstop can see the
    // authoritative DB state turn over; idle otherwise.
    refetchInterval: anyProcessing ? 15000 : false,
  });

  const patchOperation = useCallback(
    (operationId: string, patch: (op: OperationState) => OperationState) => {
      setOperations((prev) => {
        const op = prev[operationId];
        if (!op) return prev;
        return { ...prev, [operationId]: patch(op) };
      });
    },
    [],
  );

  // Reconnection: adopt any in-flight group from the DB we aren't tracking yet
  // (e.g. after a hard refresh, or an upload started on another route).
  useEffect(() => {
    setOperations((prev) => {
      let next = prev;
      for (const group of groups) {
        if (next[group.operationId] || completionHandledRef.current[group.operationId]) continue;
        const failedFiles = group.documents
          .filter((doc) => doc.status === "FAILED")
          .map((doc) => ({ fileName: doc.fileName, recordId: doc.id }));
        const processedDocIds = group.documents
          .filter((doc) => doc.status === "SUCCESS" || doc.status === "FAILED")
          .map((doc) => doc.id);
        next = {
          ...next,
          [group.operationId]: {
            ...newOperation(group.operationId, group.source as UploadSource),
            totalFiles: group.totalCount,
            processedFiles: group.processedCount,
            seenFileIds: new Set<string>(processedDocIds),
            failedFiles,
            // Reconnection has no per-file detail; counts drive the bar.
            files: [],
          },
        };
      }
      return next;
    });
  }, [groups]);

  const handleProcessingUpdate = useCallback(
    (operationId: string, progressUpdate: ProcessingStatusUpdate) => {
      if (!progressUpdate) return;
      const { recordId, status, totalFiles, fileName, failureReason } = progressUpdate;

      patchOperation(operationId, (prev) => {
        const newSeenFileIds = new Set(prev.seenFileIds);
        const newSeenSyncIds = new Set(prev.seenSyncIds);
        let newFailedFiles = prev.failedFiles;

        // Integration syncs report totalFiles per-sync (each folder is a sync).
        // Accumulate once per syncId (recordId), idempotent against replay.
        let newTotalFiles = prev.totalFiles;
        if (totalFiles && totalFiles > 0) {
          if (prev.totalFiles === 0) {
            newTotalFiles = totalFiles;
            newSeenSyncIds.add(recordId);
          } else if (
            status === "processing" &&
            prev.source !== "manual" &&
            !newSeenSyncIds.has(recordId)
          ) {
            newTotalFiles = prev.totalFiles + totalFiles;
            newSeenSyncIds.add(recordId);
          }
        }

        // A "failed" before any totalFiles means the sync itself failed (e.g.
        // SharePoint 404) before inserting documents — surface an error state.
        const isSyncLevelFailure =
          status === "failed" && prev.totalFiles === 0 && newTotalFiles === 0;
        const syncFailed = prev.syncFailed || isSyncLevelFailure;

        if (!isSyncLevelFailure) {
          if (status === "completed" || status === "failed") newSeenFileIds.add(recordId);
          if (status === "failed" && fileName) {
            const alreadyTracked = prev.failedFiles.some((f) => f.recordId === recordId);
            if (!alreadyTracked) {
              newFailedFiles = [
                ...prev.failedFiles,
                { fileName, recordId, failureReason: failureReason ?? undefined },
              ];
            }
          }
        }

        // For manual uploads, also fold the per-file status into `files`.
        let files = prev.files;
        if ((status === "completed" || status === "failed") && prev.source === "manual") {
          files = prev.files.map((s) =>
            s.documentId === recordId
              ? {
                  ...s,
                  isProcessed: status === "completed",
                  isFinished: true,
                  isError: status === "failed",
                }
              : s,
          );
        }
        const fileFinishedCount = files.filter((f) => f.isFinished).length;

        return {
          ...prev,
          totalFiles: newTotalFiles,
          processedFiles: Math.max(newSeenFileIds.size, fileFinishedCount),
          seenFileIds: newSeenFileIds,
          seenSyncIds: newSeenSyncIds,
          failedFiles: newFailedFiles,
          syncFailed,
          files,
        };
      });
    },
    [patchOperation],
  );

  // Empty integration sync (folder-only): a "completed" with no totalFiles
  // means there's nothing to track — drop the operation.
  const handleIntegrationEmptyComplete = useCallback((operationId: string) => {
    completionHandledRef.current[operationId] = true;
    setOperations((prev) => withoutOperation(prev, operationId));
  }, []);

  const handleFileCompleted = useCallback(
    async (recordId: string) => {
      // Refresh the document list once per completed file (dedup against replay).
      if (invalidatedFileIdsRef.current.has(recordId)) return;
      invalidatedFileIdsRef.current.add(recordId);
      await queryClient.invalidateQueries({ queryKey: ["customerDocuments"] });
    },
    [queryClient],
  );

  // Per-operation completion + backstop, evaluated over the whole map. Hooks
  // can't loop, so one effect applies the rules to every operation.
  useEffect(() => {
    for (const op of Object.values(operations)) {
      if (!op.filesProcessing) continue;
      const operationGroupPresent = groups.some((g) => g.operationId === op.operationId);
      if (operationGroupPresent) observedGroupRef.current[op.operationId] = true;

      // Manual: complete once every file has settled.
      if (op.source === "manual" && op.files.length > 0) {
        const finishedCount = op.files.filter((f) => f.isFinished).length;
        if (finishedCount === op.files.length) {
          patchOperation(op.operationId, (o) => ({ ...o, filesProcessing: false }));
          continue;
        }
      }

      // Manual backstop: a previously-seen DB group disappearing means the
      // backend has settled — finish any stragglers a missed event left behind.
      if (
        backstopShouldFinish({
          filesProcessing: op.filesProcessing,
          isManualUpload: op.source === "manual",
          files: op.files,
          operationGroupPresent,
          hasObservedGroup: !!observedGroupRef.current[op.operationId],
        })
      ) {
        // A re-adopted operation has counts but no per-file detail, so there is
        // nothing to mark finished and its bar can only ever show the stale
        // "Processing 0 of N files". Drop the operation instead, which clears
        // both the bar and the "upload in progress" guard.
        if (op.files.length === 0) {
          completionHandledRef.current[op.operationId] = true;
          setOperations((prev) => withoutOperation(prev, op.operationId));
          continue;
        }
        patchOperation(op.operationId, (o) => ({
          ...o,
          files: o.files.map((f) => (f.isFinished ? f : { ...f, isFinished: true })),
        }));
        continue;
      }

      // Integration / count-based: once processedFiles catches totalFiles,
      // verify against the DB (multi-folder syncs may under-report totalFiles).
      if (op.totalFiles > 0 && op.processedFiles >= op.totalFiles) {
        if (op.source !== "manual" && !verifyingRef.current[op.operationId]) {
          verifyingRef.current[op.operationId] = true;
          void refetchActiveUploads()
            .then(({ data }) => {
              const dbGroup = data?.activeUploads?.groups?.find(
                (g) => g.operationId === op.operationId && g.source === op.source,
              );
              if (dbGroup && dbGroup.processedCount < dbGroup.totalCount) {
                patchOperation(op.operationId, (o) => ({
                  ...o,
                  totalFiles: dbGroup.totalCount,
                  processedFiles: dbGroup.processedCount,
                }));
                return;
              }
              completionHandledRef.current[op.operationId] = true;
              patchOperation(op.operationId, (o) => ({ ...o, filesProcessing: false }));
            })
            .finally(() => {
              verifyingRef.current[op.operationId] = false;
            });
          continue;
        }
        if (op.source === "manual") {
          completionHandledRef.current[op.operationId] = true;
          patchOperation(op.operationId, (o) => ({ ...o, filesProcessing: false }));
        }
      }
    }
  }, [operations, groups, patchOperation, refetchActiveUploads]);

  const resetLatches = (operationId: string) => {
    completionHandledRef.current[operationId] = false;
    observedGroupRef.current[operationId] = false;
    verifyingRef.current[operationId] = false;
  };

  const setSync = useCallback(
    (sync: SyncToProcess | null, operationId?: string) => {
      const id = operationId ?? currentUser?.guid;
      if (!id) return;
      if (!sync) {
        setOperations((prev) => withoutOperation(prev, id));
        return;
      }
      resetLatches(id);
      setOperations((prev) => ({
        ...prev,
        [id]: newOperation(id, sync.integration as UploadSource),
      }));
    },
    [currentUser],
  );

  const startProcessing = useCallback(
    (files: FileWithDirectoryPath[], operationId?: string) => {
      const id = operationId ?? currentUser?.guid;
      if (!id) return;
      resetLatches(id);
      setOperations((prev) => ({
        ...prev,
        [id]: {
          ...newOperation(id, "manual"),
          totalFiles: files.length,
          files: files.map(blankFileStatus),
        },
      }));
    },
    [currentUser],
  );

  // Locate the file by documentId (globally unique) or fileName. When the
  // caller knows the operationId, matching is scoped to that single operation
  // so a fileName fallback can't cross over to a same-named file in another
  // concurrent operation; otherwise we scan every manual operation.
  const updateFileStatus = useCallback<UpdateFileStatus>(
    ({ operationId, fileName, documentId, updates }) => {
      setOperations((prev) => {
        const scoped = operationId && prev[operationId] ? [prev[operationId]] : Object.values(prev);
        let next = prev;
        for (const op of scoped) {
          if (op.source !== "manual") continue;
          let changed = false;
          const files = op.files.map((status) => {
            const isMatch = documentId
              ? status.documentId === documentId ||
                (!status.documentId && !!fileName && status.file.name === fileName)
              : !!fileName && status.file.name === fileName;
            if (!isMatch) return status;
            changed = true;
            return { ...status, ...updates, documentId: documentId ?? status.documentId };
          });
          if (changed) next = { ...next, [op.operationId]: { ...op, files } };
        }
        return next;
      });
    },
    [],
  );

  const addStatusesForFiles = useCallback(
    (newFiles: FileWithDirectoryPath[], operationId?: string) => {
      const id = operationId ?? currentUser?.guid;
      if (!id) return;
      patchOperation(id, (op) =>
        op.source !== "manual"
          ? op
          : { ...op, files: newFiles.map(blankFileStatus), totalFiles: newFiles.length },
      );
    },
    [patchOperation, currentUser],
  );

  const removeStatusForFile = useCallback(
    (fileName: string, operationId?: string) => {
      const id = operationId ?? currentUser?.guid;
      if (!id) return;
      patchOperation(id, (op) => {
        if (op.source !== "manual") return op;
        const files = op.files.filter((s) => s.file.name !== fileName);
        return { ...op, files, totalFiles: files.length };
      });
    },
    [patchOperation, currentUser],
  );

  const dismissOperation = useCallback((operationId: string) => {
    resetLatches(operationId);
    setOperations((prev) => withoutOperation(prev, operationId));
  }, []);

  const contextValue = useMemo<DocumentUploadProgressContextType>(
    () => ({
      operations,
      setSync,
      startProcessing,
      updateFileStatus,
      addStatusesForFiles,
      removeStatusForFile,
      dismissOperation,
    }),
    [
      operations,
      setSync,
      startProcessing,
      updateFileStatus,
      addStatusesForFiles,
      removeStatusForFile,
      dismissOperation,
    ],
  );

  const visibleOperations = Object.values(operations).filter((op) => op.show);

  return (
    <DocumentUploadProgressContext.Provider value={contextValue}>
      {children}

      {/* One subscription per active operation so uploads stream concurrently. */}
      {Object.values(operations)
        .filter((op) => op.filesProcessing)
        .map((op) => (
          <OperationProcessingSubscription
            key={op.operationId}
            operationId={op.operationId}
            source={op.source}
            onUpdate={handleProcessingUpdate}
            onIntegrationEmptyComplete={handleIntegrationEmptyComplete}
            onFileCompleted={handleFileCompleted}
          />
        ))}

      {/* One progress bar per visible operation, stacked. */}
      {visibleOperations.length > 0 && (
        <ProgressBarStack>
          {visibleOperations.map((op) => (
            <UploadProgressBar
              key={op.operationId}
              processedFiles={op.processedFiles}
              totalFiles={op.totalFiles}
              failedFiles={op.failedFiles}
              syncFailed={op.syncFailed}
              onDismiss={() => dismissOperation(op.operationId)}
            />
          ))}
        </ProgressBarStack>
      )}
    </DocumentUploadProgressContext.Provider>
  );
}

// Stacks the per-operation progress bars; each UploadProgressBar manages its
// own card styling.
const ProgressBarStack = styled.div({
  position: "fixed",
  bottom: 24,
  right: 24,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  zIndex: 1000,
});

/**
 * Read upload progress for a single operationId (a bidId, or the current user's
 * guid for content-library / integration uploads). Returns idle values when
 * there's no operation for that id. Actions (startProcessing, setSync, …) are
 * global and identical regardless of the operationId passed.
 */
export function useDocumentUploadProgress(operationId?: string) {
  const context = useContext(DocumentUploadProgressContext);
  const { data: currentUser } = useCurrentUser();
  if (!context) {
    throw new Error("useDocumentUploadProgress must be used within DocumentUploadProgressProvider");
  }

  const id = operationId ?? currentUser?.guid;
  const op = id ? context.operations[id] : undefined;

  const scoped: ScopedProgress = useMemo(() => {
    if (!op) return IDLE_SCOPE;
    const uploadStatuses = op.source === "manual" ? op.files : [];
    const { allUploaded, allFinished, allProcessed } = getUploadProgressStatus(uploadStatuses);
    return {
      filesProcessing: op.filesProcessing,
      uploadStatuses,
      allUploaded,
      allFinished,
      allProcessed,
      filesCount: uploadStatuses.length,
    };
  }, [op]);

  return {
    ...scoped,
    setSync: context.setSync,
    startProcessing: context.startProcessing,
    updateFileStatus: context.updateFileStatus,
    addStatusesForFiles: context.addStatusesForFiles,
    removeStatusForFile: context.removeStatusForFile,
    dismissOperation: context.dismissOperation,
  };
}

/**
 * Manual-upload statuses for every operation, keyed by resourceId. Lets a single
 * consumer observe all bids' upload progress at once (replacing one
 * `useDocumentUploadProgress(bidId)` per bid) while still exposing only file
 * statuses — not the internal Operation shape. Reactive: re-derives whenever the
 * operations map changes.
 */
export function useUploadStatusesByResource(): Record<string, FileUploadStatus[]> {
  const context = useContext(DocumentUploadProgressContext);
  if (!context) {
    throw new Error(
      "useUploadStatusesByResource must be used within DocumentUploadProgressProvider",
    );
  }
  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries(context.operations).map(([resourceId, op]) => [
          resourceId,
          op.source === "manual" ? op.files : [],
        ]),
      ),
    [context.operations],
  );
}
